import {
  AlertCircle,
  Calendar,
  Clock,
  FileText,
  PhoneForwarded,
  Sparkles,
} from 'lucide-react';

import { IndustryPage, type IndustryPageConfig } from '@/components/marketing/industry-page';

const config: IndustryPageConfig = {
  seoTitle: 'AI Receptionist for Medical Clinics — Triage Calls, Capture Appointments',
  seoDescription:
    'An AI receptionist built for GP clinics and medical practices. Triages routine calls, captures appointment requests, escalates urgent patients, and answers FAQs — 24/7.',
  path: '/industries/medical',
  badge: 'Medical Clinics',
  headline: 'Triage routine calls. Capture every appointment. Escalate what matters.',
  subheadline:
    'Your AI receptionist handles the call volume your front desk can\'t keep up with — answering FAQs, taking appointment requests, and escalating urgent patients to your team instantly.',
  pains: [
    'Patients on hold abandon calls and seek care elsewhere — or show up unannounced.',
    'Front desk staff spend hours on repeat questions: hours, scripts, referral process.',
    'Urgent calls get lost in a queue alongside routine appointment requests.',
  ],
  features: [
    {
      icon: AlertCircle,
      title: 'Urgency triage',
      desc: 'Detects urgency signals in caller language and immediately transfers to the duty nurse or on-call doctor — no hold time for emergencies.',
    },
    {
      icon: Calendar,
      title: 'Appointment request capture',
      desc: 'Captures patient name, DOB, reason for visit, and preferred time. Logged in your dashboard for front desk confirmation.',
    },
    {
      icon: Sparkles,
      title: 'Answers from your clinical FAQ',
      desc: 'Script availability, referral process, bulk billing policy, opening hours — all answered from your uploaded content.',
    },
    {
      icon: PhoneForwarded,
      title: 'Smart call routing',
      desc: 'Route by intent: prescription repeats to reception, results queries to nursing staff, emergencies to duty doctor.',
    },
    {
      icon: Clock,
      title: '24/7 coverage',
      desc: 'After-hours calls answered. Appointment requests captured overnight and ready for your team each morning.',
    },
    {
      icon: FileText,
      title: 'Call log & summaries',
      desc: 'Full transcript and AI summary for every call. Review, triage, and act from one dashboard.',
    },
  ],
  faqs: [
    {
      q: 'Is this HIPAA / privacy compliant?',
      a: 'The agent captures non-clinical intake data (name, contact, preferred time). It does not access or store medical records. Consult your compliance team for specific requirements.',
    },
    {
      q: 'Can the AI handle prescription repeat requests?',
      a: 'It can capture the request details and route them appropriately. Define your intake workflow and the agent follows it.',
    },
    {
      q: 'What happens if a patient sounds seriously unwell?',
      a: 'You define urgency transfer rules. The agent recognises distress or urgency cues and warm-transfers immediately to your on-call line.',
    },
    {
      q: 'Can it handle multiple doctors with different schedules?',
      a: 'Yes — your knowledge base can include per-doctor availability and the agent routes accordingly.',
    },
  ],
  ctaHeadline: 'Give your front desk their time back.',
  ctaSub: 'Live in under 10 minutes. No long-term contract required.',
};

export function MedicalIndustryPage(): JSX.Element {
  return <IndustryPage config={config} />;
}
